import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormButtonType } from 'app/services/api5-service/api.interface';
import {
  getButtonList,
  isSubmitButtonUi,
} from 'components/form-button/form-button.utils';
import { IFormButtonUi } from 'components/form-button/model';
import { BehaviorSubject, combineLatest, merge, of } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

import { FormService } from './form.service';
import { HiddenService } from './hidden.service';
import { SetValuesService } from './set-values.service';

import { isFormSubmittable } from '../utils/is-form-submitable';

@UntilDestroy()
@Injectable()
export class ButtonsService {
  /** Form footer buttons */
  private readonly footerButtonsSubject: BehaviorSubject<IFormButtonUi[]> =
    new BehaviorSubject([]);

  /** All form lists buttons */
  private readonly listsButtonsSubject: BehaviorSubject<
    Map<string, IFormButtonUi[]>
  > = new BehaviorSubject(new Map());

  /** All form buttons */
  private readonly buttonsSubject: BehaviorSubject<IFormButtonUi[]> =
    new BehaviorSubject([]);

  /** All form buttons */
  private readonly formButtons$ = combineLatest([
    this.footerButtonsSubject,
    this.listsButtonsSubject,
    this.buttonsSubject,
  ]).pipe(
    map(([footerButtons, listsButtons, buttons]) => {
      const allListsButtons = Array.from(listsButtons.values()).flat();
      return footerButtons.concat(allListsButtons).concat(buttons);
    }),
  );

  readonly footerButtons$ = this.footerButtonsSubject.asObservable();

  readonly isHiddenFooterButtons$ = this.footerButtons$.pipe(
    switchMap(footerButtons =>
      footerButtons.length === 0
        ? of(true)
        : combineLatest(
            footerButtons.map(button =>
              this.hiddenService.isHidden$(button.name),
            ),
          ).pipe(
            map(buttonsHiddenState =>
              buttonsHiddenState.every(isHidden => isHidden),
            ),
          ),
    ),
  );

  constructor(
    private readonly formService: FormService,
    private readonly setValuesService: SetValuesService,
    private readonly hiddenService: HiddenService,
  ) {
    this.subscribeToDocChange();
    this.subscribeToSetValuesChanges();

    this.subscribeToValueChange();
    this.subscribeToButtonsPreloadingChange();
  }

  private clear(): void {
    this.footerButtonsSubject.next([]);
    this.listsButtonsSubject.next(new Map());
    this.buttonsSubject.next([]);
  }

  /**
   * Watch for doc footer buttons
   */
  private subscribeToDocChange(): void {
    this.formService.doc$.pipe(untilDestroyed(this)).subscribe(doc => {
      this.clear();

      const buttons = getButtonList(
        doc,
        doc.metadata?.form?.buttons?.button || [],
        this.formService.formState.showHints,
      );
      this.setFooterButtons(buttons);
    });
  }

  /**
   * Buttons can be changed during set values. Subscribe to this event
   */
  private subscribeToSetValuesChanges(): void {
    this.setValuesService.setValuesRequestEvent$
      .pipe(untilDestroyed(this))
      .subscribe(event => {
        if ('result' in event && event.result.setValuesDoc.buttons?.button) {
          const newButtons = getButtonList(
            this.formService.loadedDoc,
            event.result.setValuesDoc.buttons.button,
            this.formService.formState.showHints,
          );

          this.setFooterButtons(newButtons);

          this.updateFooterButtonsDisabledState();
        }
      });
  }

  private disableAllButtons(
    buttons: IFormButtonUi[],
    exceptButtonIds: string[],
  ): void {
    buttons
      .filter(({ id }) => !exceptButtonIds.includes(id))
      .forEach(({ disabledSubject }) => disabledSubject.next(true));
  }

  private restoreButtonsState(buttons: IFormButtonUi[]): void {
    buttons.forEach(({ disabledSubject, originalDisabled }) =>
      disabledSubject.next(originalDisabled),
    );

    this.updateFooterButtonsDisabledState();
  }

  private updateFooterButtonsDisabledState(): void {
    const isSubmittable = isFormSubmittable(this.formService.configs);
    this.footerButtonsSubject.value
      .filter(button => isSubmitButtonUi(button))
      .forEach(button => button.disabledSubject.next(!isSubmittable));
  }

  /**
   * Subscribe to form status changes
   */
  private subscribeToValueChange(): void {
    merge(
      this.formService.valueChanges$,
      this.formService.formGroup$.pipe(
        switchMap(formGroup => formGroup.statusChanges),
      ),
    )
      // @TODO this uses a delay to ensure that formly receives all the changes and returns
      // the correct form submittable value, and properly disables or undisables the form buttons.
      // It is advisable to somehow get away from this and get on the status of form validation
      .pipe(debounceTime(50), untilDestroyed(this))
      .subscribe(() => {
        this.updateFooterButtonsDisabledState();
      });
  }

  /**
   * Subscribe to buttons preloader changing
   * UX ask us to disable ALL other buttons, if some button have preloader
   * this is what happend here
   */
  private subscribeToButtonsPreloadingChange(): void {
    this.formButtons$
      .pipe(
        switchMap(allButtons =>
          combineLatest(
            allButtons.map(button =>
              button.preloaderSubject.pipe(
                tap(preloader => {
                  if (preloader) {
                    // if preloader turns on - then disable all other buttons
                    this.disableAllButtons(allButtons, [button.id]);
                  } else {
                    // else restore normal buttons disabled state
                    this.restoreButtonsState(allButtons);
                  }
                }),
              ),
            ),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  getFirstSubmitButtonOnForm(): IFormButtonUi | undefined {
    const visibleFooterButtons = this.footerButtonsSubject.value.filter(
      button => !this.hiddenService.isHidden(button.name),
    );
    const allButtons = this.buttonsSubject.value;
    if (visibleFooterButtons.length > 0) {
      return (
        visibleFooterButtons.find(isSubmitButtonUi) ||
        visibleFooterButtons.find(
          button => button.type === FormButtonType.SetValues,
        )
      );
    }
    return (
      allButtons.find(isSubmitButtonUi) ||
      allButtons.find(button => button.type === FormButtonType.SetValues)
    );
  }

  getFooterButtons(): IFormButtonUi[] {
    return this.footerButtonsSubject.value;
  }

  setFooterButtons(buttons: IFormButtonUi[]): void {
    this.footerButtonsSubject.next(buttons);
  }

  setListButtons(name: string, buttons: IFormButtonUi[]): void {
    this.listsButtonsSubject.value.set(name, buttons);
    this.listsButtonsSubject.next(this.listsButtonsSubject.value);
  }

  addButton(button: IFormButtonUi, replaceExisted?: boolean): void {
    if (replaceExisted) {
      const existedButtonIndex = this.buttonsSubject.value.findIndex(
        b => b.name === button.name,
      );
      if (existedButtonIndex >= 0) {
        this.buttonsSubject.value.splice(existedButtonIndex, 1);
      }
    }

    this.buttonsSubject.value.push(button);
    this.buttonsSubject.next(this.buttonsSubject.value);
  }
}
